import React, { Fragment, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Switch,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import { AlertCircle } from 'react-feather';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import Book from '@mui/icons-material/Book';
import { Modal, ModalHeader, ModalBody, Alert } from 'reactstrap';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import CustomTable from '../tables/MuiDataGrid';

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | Projects';
  }, []);
  const [modalState, setModalState] = React.useState({
    update: false,
    open: false,
  });
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState('All');
  const [appliedFilters, setAppliedFilters] = React.useState([]);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [tableData, setTableData] = React.useState({
    count: 0,
    projects: [],
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    getValues,
    // clearErrors,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  React.useEffect(() => {
    trigger();
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  React.useEffect(() => {
    setDataIsFetching(true);

    let requestUrl = `/projects?per_page=${perPage}&page=${page}`;

    if (statusFilter === 'ACTIVE') {
      requestUrl += '&is_active=true';
      setAppliedFilters(['is_active']);
    } else if (statusFilter === 'INACTIVE') {
      requestUrl += '&is_active=false';
      setAppliedFilters(['is_active']);
    } else {
      setAppliedFilters([]);
    }

    userRequest()
      .get(requestUrl)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          projects: data.projects,
        });

        setDataIsFetching(false);
      })
      .catch(({ response }) => {
        console.error(response);
        setDataIsFetching(false);
      });
  }, [statusFilter, perPage, page]);

  function setStatus(id, data) {
    userRequest()
      .post(`/projects/update/${id}`, data)
      .then(() => {
        let projs = [];
        if (meetFilterConditions(data)) {
          projs = tableData.projects.map((proj) => {
            if (id === proj.id) proj.is_active = !proj.is_active;
            return proj;
          });
        } else {
          projs = tableData.projects.filter((proj) => proj.id !== id);
        }
        console.log(projs);
        setTableData({
          count: projs.length,
          projects: projs,
        });
      })
      .catch(() => {});
  }

  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }
    if (statusFilter === 'ALL') {
      return true;
    }
    if (appliedFilters.length > 0) return statusFilter === data.is_active;

    return true;
  }

  const actionMethods = {
    toggleEdit: (row) => {
      setModalState({ open: true, update: row.id });
      reset(
        Object.keys(defaultValues).reduce((reduced, curr) => {
          reduced[curr] = row[curr];
          return reduced;
        }, {})
      );
      // clearErrors();
    },
    toggleActive: (row) => setStatus(row.id, { is_active: !row.is_active }),
  };

  const columns = [
    {
      field: 'display_name',
      headerName: 'Project',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  function getFilters() {
    if (statusFilter === 'ACTIVE') {
      return ['Status: Active'];
    } else if (statusFilter === 'INACTIVE') {
      return ['Status: Inactive'];
    } else {
      return [];
    }
  }

  const onSubmit = (data) => {
    if (data !== '') {
      toast.info(`${modalState.update ? 'Updating...' : 'Creating...'}`, {
        isLoading: true,
        position: 'bottom-center',
        autoClose: 5000,
        theme: 'colored',
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'create',
      });
      let api = modalState.update ? `/projects/update/${modalState.update}` : '/projects';

      userRequest()
        .post(api, data)
        .then((res) => {
          toast.dismiss('create');
          toast.success(`Successfully ${modalState.update ? 'updated' : 'created'}`, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
          const project = res.data.project;
          let projects = [];
          if (modalState.update) {
            projects = tableData.projects.reduce((pre, curr) => {
              if (project.id === curr.id) {
                meetFilterConditions({ ...curr, ...project }, true) && pre.push({ ...curr, ...project });
              } else pre.push(curr);

              return pre;
            }, []);
          } else {
            projects = meetFilterConditions(data, false) ? [...tableData.projects, project] : tableData.projects;
          }
          setTableData({ count: projects.length, projects });
          setModalState({ update: true, open: false });
          reset(defaultValues, {});
        })
        .catch(({ response }) => {
          toast.dismiss('create');
          toast.error(response.data.message, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
        });
    }
  };

  return (
    <Fragment>
      <Breadcrumb icon={<Book />} title="Manage" parent="Projects" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <div className="form-group row mx-0">
            <FormControl>
              <InputLabel id="demo-simple-select-helper-label">Status</InputLabel>
              <Select
                name="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                labelId="demo-simple-select-helper-label"
                id="demo-simple-select-helper"
                label="Status"
                style={{
                  fontStyle: statusFilter === 'All' ? 'italic' : 'normal',
                }}
              >
                <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                  All
                </MenuItem>
                <MenuItem value={'ACTIVE'}>Active</MenuItem>
                <MenuItem value={'INACTIVE'}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </div>
        </FilterAccordian>
      </div>
      <button
        onClick={() => {
          setModalState({ update: false, open: true });
          // clearErrors();
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <Modal
        isOpen={modalState.open}
        toggle={() => {
          setModalState({ update: false, open: false });
          reset(defaultValues, {});
        }}
        size="lg"
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setModalState({ update: false, open: false });
          }}
        >
          {modalState.update ? 'Edit' : 'Create'} Project
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group row">
                <Alert className=" d-flex gap-3" color="secondary dark">
                  <AlertCircle />
                  <p>Please note: The name of this project must match name in accounting system.</p>
                </Alert>
              </div>
              <div className="form-group row">
                <TextField
                  label="Name"
                  variant="outlined"
                  {...register(`display_name`)}
                  error={!!(errors.display_name?.message && isSubmitted && getValues().display_name === '')}
                  // helperText={errors.display_name?.message && isSubmitted ? errors.display_name.message : ''}

                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>
              <div className="d-flex justify-content-start my-4">
                {modalState.update && (
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch {...register(`is_active`)} checked={watch('is_active')} />}
                      label="Active Status"
                      sx={{ margin: 0 }}
                      labelPlacement="start"
                    />
                  </FormGroup>
                )}
              </div>
              <div>
                <button className="btn btn-primary me-1" type="submit">
                  {modalState.update ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </ModalBody>
      </Modal>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.projects}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
        />
      </div>
    </Fragment>
  );
};

const defaultValues = {
  display_name: '',
  is_active: true,
};

const validationSchema = Yup.object().shape({
  display_name: Yup.string().required('Required'),
  is_active: Yup.boolean().required(),
});

export default Manage;
